/**
 * WorkforceSchedule — the flagship Workforce surface: a staff × time day grid
 * for one location + week, with a demand ribbon stacked from the three demand
 * sources (events / appointments / walk-in queues) and a draft → publish flow.
 *
 * Data: getScheduleWeek (declared op). Mutations: createShift / updateShift /
 * deleteShift / publishScheduleWeek — all awaited; success feedback only after
 * resolve (no-fake-interactivity mandate).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Btn, Icon, Input, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { Drawer } from "../../shell/Overlay";
import { useNav } from "../../shell/router";
import { DataSourceStrip } from "../../shell/opSource";
import { FilterBar, EmptyState, FilterDropdown } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useIsMobile } from "../../shell/useResponsive";
import { useWfResource, useBusy } from "./useWorkforce";
import WorkforceScheduleWeek from "./WorkforceScheduleWeek";
import {
  getScheduleWeek, createShift, updateShift, deleteShift, publishScheduleWeek, getOpsBoard,
  listLaborRules, evaluateShiftRules, previewPublishImpact, discardScheduleDraft, syncBookings,
  type ScheduleWeek, type WfShift, type OpsBoardSnapshot, type OpsAppointment,
  type LaborRule, type ShiftRuleEvaluation, type PublishImpact, type WfStaffLite,
} from "./wf-api";
import {
  WeekNav, LocationFilter, SourceLegend, DemandStack, WfPending,
  mondayOf, isoToday, weekDates, dayLabel, hmToMin, hmLabel, hmRangeLabel, minToHm,
  labelToHm, roleHue, pad2,
} from "./wf-shared";
import { useT, t as i18nT } from "../../lib/i18n";

/** Grid geometry (Schedule port stage 1): slot width per granularity, row height per density. */
type Density = "compact" | "comfortable" | "spacious";
const ROW_H_BY_DENSITY: Record<Density, number> = { compact: 40, comfortable: 56, spacious: 68 };
const SLOT_W_BY_MIN: Record<number, number> = { 15: 32, 30: 26 };
const STAFF_W = 232;
/** Hours of slack the day grid shows either side of the day's shift coverage. */
const WINDOW_MARGIN_H = 2;
/** The grid opens scrolled to this hour when the window starts earlier. */
const SCROLL_SEED_HOUR = 8;
const DENSITY_ORDER: Density[] = ["compact", "comfortable", "spacious"];

/** A refused request often carries no message; never report an empty reason. */
function reasonOf(e: unknown): string {
  const msg = e instanceof Error ? e.message : typeof e === "string" ? e : "";
  return msg.trim() || "the request was refused without a reason";
}
const DENSITY_LABELS: Record<Density, string> = { compact: "Compact", comfortable: "Comfortable", spacious: "Spacious" };

/** Heads scheduled per slot of `slotMinutes` for one day (breaks subtract). */
function scheduledPerSlot(shifts: WfShift[], date: string, startHour: number, slots: number, slotMinutes: number): number[] {
  const out = new Array(slots).fill(0);
  const base = startHour * 60;
  shifts.filter((s) => s.date === date && s.staffId).forEach((s) => {
    const a = hmToMin(s.start), b = hmToMin(s.end);
    for (let i = 0; i < slots; i++) {
      const t0 = base + i * slotMinutes, t1 = t0 + slotMinutes;
      if (a < t1 && b > t0 && !s.breaks.some((br) => hmToMin(br.start) < t1 && hmToMin(br.end) > t0)) out[i] += 1;
    }
  });
  return out;
}

type SortKey = "default" | "name" | "role" | "skills" | "booked";
const sortLabels = (t: (k: string, f?: string) => string): Record<SortKey, string> => ({
  default: t("workforce.schedule.sortDefaultOrder", "Default order"), name: t("workforce.schedule.sortNameAZ", "Name A–Z"), role: t("workforce.schedule.sortRole", "Role"), skills: t("workforce.schedule.sortSkillBreadth", "Skill breadth"), booked: t("workforce.schedule.sortBookedLoad", "Booked load"),
});

interface DrawerState { shift: WfShift; isNew: boolean; staffIds?: string[]; dateTo?: string }
interface DragState { aSlot: number; bSlot: number; aRow: number; bRow: number }

/** One figure in the forecast card's header row. `note` carries the qualifier
 *  (peak hour, unit, or why a figure is unavailable) so a dash is never bare. */
function ForecastStat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: string }) {
  return (
    <div style={{ minWidth: 92 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: T.soft, whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 3 }}>
        <span style={{ fontSize: 25, fontWeight: 600, lineHeight: 1.05, color: tone ?? T.n1, fontFamily: T.fontMono }}>{value}</span>
        {note ? <span style={{ fontSize: 11, color: T.soft }}>{note}</span> : null}
      </div>
    </div>
  );
}

export default function WorkforceSchedule() {
  const t = useT();
  const SORT_LABELS = sortLabels(t);
  const nav = useNav();
  const isMobile = useIsMobile();
  /* The page header can hold the seven-segment day strip only on a wide screen;
   * below this the title wraps and the strip drops back under the toolbar. */
  const isNarrow = useIsMobile(1280);
  const [weekStart, setWeekStart] = useState(() => mondayOf());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [day, setDay] = useState(() => isoToday());
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [density, setDensity] = useState<Density>("comfortable"); // ui-local (view preference)
  const [appt, setAppt] = useState<OpsAppointment | null>(null); // ui-local (which appointment's detail is open)
  const [impact, setImpact] = useState<{ shift: WfShift; appts: OpsAppointment[] } | null>(null); // ui-local (remove-shift review)
  const [serviceFilter, setServiceFilter] = useState<string | null>(null); // ui-local (view filter)
  const [sort, setSort] = useState<SortKey>("default"); // ui-local (row order)
  const [hideAppts, setHideAppts] = useState(false); // ui-local (shifts-only view)
  /* The forecast chart/narrative and the per-LOB tiles are opt-in detail. Pinned
   * open they took ~370px above the fold and left the day grid — the thing the
   * screen is for — as a ~60px strip with its own scrollbar. Collapsed (default)
   * the summary figures still read on the card's header row and the grid fills
   * the screen. Remembered per browser. */
  const [details, setDetails] = useState(() => { // ui-local (view preference)
    try { return window.localStorage.getItem("wfschedule.details") === "1"; } catch { return false; }
  });
  const toggleDetails = () => setDetails((v) => {
    const next = !v;
    try { window.localStorage.setItem("wfschedule.details", next ? "1" : "0"); } catch { /* storage unavailable */ }
    return next;
  });
  const [mode, setMode] = useState<"day" | "week">(() => (new URLSearchParams(window.location.search).get("mode") === "week" ? "week" : "day"));
  const setModeUrl = (next: "day" | "week") => {
    setMode(next);
    const u = new URL(window.location.href);
    if (next === "week") u.searchParams.set("mode", "week"); else u.searchParams.delete("mode");
    window.history.replaceState(window.history.state, "", u.toString());
  };
  const [checks, setChecks] = useState<PublishImpact | null>(null); // pre-publish impact (server-computed)
  const [checksError, setChecksError] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null); // ui-local (in-flight lane selection)
  const [hoverRow, setHoverRow] = useState<number | null>(null); // ui-local (empty-lane affordance)
  const rowsRef = useRef<HTMLDivElement | null>(null);
  // Geometry lives in a ref as well as state: pointerdown → pointerup can land in
  // one task (a fast click), and a state read in the release handler would still
  // see the pre-drag closure value and silently create nothing.
  const dragRef = useRef<DragState | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const seededScroll = useRef(false);
  const { busy, run } = useBusy();

  const key = `${weekStart}|${locationId ?? "all"}`;
  const { data: week, loading, error, reload } = useWfResource<ScheduleWeek>(`schedule:${key}`, () => getScheduleWeek(weekStart, locationId ?? undefined));

  // Booked appointments for the selected day. Separate op (getOpsBoard) because
  // ScheduleWeek carries demand only in aggregate — see REQ-096. A day the
  // server has no board for simply has no appointment layer; it never blocks
  // the grid, so this resource's error is deliberately not surfaced.
  const { data: board } = useWfResource<OpsBoardSnapshot>(`opsboard:${day}|${locationId ?? "all"}`, () => getOpsBoard(day, locationId ?? undefined));
  // Live labor rules — the compliance panel shows the rule set and its real
  // parameters, so an admin edit in Rules & policies is reflected here.
  const { data: laborRules } = useWfResource<{ rules: LaborRule[] }>("laborrules", listLaborRules);

  const days = weekDates(weekStart);
  const activeDay = days.includes(day) ? day : days[0];
  const canWrite = can("schedules.write");
  const canPublish = can("schedules.publish");
  /* Discarding a week of drafts is separable from editing (stage 7). Reassigning an
   * appointment stays on schedules.write: the appointments.* capability family is
   * frozen pending Claude Code's auth wave, so CD must not author a cap id for it. */
  const canDiscard = can("schedules.discard");

  const win = week?.window ?? { startHour: 8, endHour: 20 };
  const slotMinutes = win.slotMinutes ?? 15;
  const slotW = SLOT_W_BY_MIN[slotMinutes] ?? 32;
  const slotsPerHour = Math.max(1, Math.round(60 / slotMinutes));
  /* Two windows, deliberately (operator, 2026-07-30):
   *  · `win` is the SERVED window and stays the demand window — the demand
   *    slices are hourly slices OF IT, so rescaling them to a wider grid would
   *    draw demand at hours it never described.
   *  · `gridWin` is what the lanes render: two hours either side of the day's
   *    real shift coverage, so an early-open or late-close shift can be
   *    scrolled to instead of being clipped at the served edge. It only ever
   *    WIDENS `win`, so no demand slice is ever hidden, and the ribbon is
   *    OFFSET by `demandLeadHours` instead of stretched. */
  const gridWin = useMemo(() => {
    const ds = (week?.shifts ?? []).filter((s) => s.date === activeDay);
    if (!ds.length) return { startHour: win.startHour, endHour: win.endHour };
    const first = Math.min(...ds.map((s) => hmToMin(s.start)));
    const last = Math.max(...ds.map((s) => hmToMin(s.end)));
    return {
      startHour: Math.max(0, Math.min(win.startHour, Math.floor(first / 60) - WINDOW_MARGIN_H)),
      endHour: Math.min(24, Math.max(win.endHour, Math.ceil(last / 60) + WINDOW_MARGIN_H)),
    };
  }, [week, activeDay, win.startHour, win.endHour]);
  const demandHours = Math.max(1, win.endHour - win.startHour);
  const demandLeadHours = Math.max(0, win.startHour - gridWin.startHour);
  const hours = Math.max(1, gridWin.endHour - gridWin.startHour);
  const slots = hours * slotsPerHour;
  const gridW = slots * slotW;
  const rowH = ROW_H_BY_DENSITY[density];
  const timeOptions = useMemo(() => {
    const out: string[] = [];
    for (let m = gridWin.startHour * 60; m <= gridWin.endHour * 60; m += slotMinutes) out.push(hmLabel(minToHm(m)));
    return out;
  }, [gridWin.startHour, gridWin.endHour, slotMinutes]);

  const dayShifts = useMemo(() => (week?.shifts ?? []).filter((s) => s.date === activeDay), [week, activeDay]);
  const openShifts = dayShifts.filter((s) => !s.staffId);
  const dayAppts = board?.appointments ?? [];
  /** Appointments land in a staff lane by name — the ops board carries no staffId (REQ-096). */
  const apptsByStaffName = useMemo(() => {
    const map = new Map<string, OpsAppointment[]>();
    (board?.appointments ?? []).forEach((a) => {
      const list = map.get(a.staffName) ?? [];
      list.push(a);
      map.set(a.staffName, list);
    });
    return map;
  }, [board]);
  const dayDemand = week?.demand?.find((d) => d.date === activeDay);
  /* The forecast's slice length is whatever the server sent — this location
   * sends 20 slices for a 10-hour window (half-hourly), not one per hour.
   * Everything downstream (capacity comparison, chart x-scale, axis labels,
   * peak time) derives from it; assuming hourly slices double-counted the
   * short slots and drew the two series on different scales. */
  const demandSlices = dayDemand?.slices ?? [];
  const demandStepMin = demandSlices.length ? Math.max(5, Math.round((demandHours * 60) / demandSlices.length)) : 60;
  const scheduled = useMemo(
    () => scheduledPerSlot(dayShifts, activeDay, win.startHour, demandSlices.length || demandHours, demandStepMin),
    [dayShifts, activeDay, win.startHour, demandSlices.length, demandHours, demandStepMin],
  );
  const demandMax = useMemo(() => {
    const totals = (dayDemand?.slices ?? []).map((s) => s.events + s.appointments + s.queue);
    return Math.max(1, ...totals, ...scheduled);
  }, [dayDemand, scheduled]);
  const shortSlots = (dayDemand?.slices ?? []).filter((s, i) => s.events + s.appointments + s.queue > (scheduled[i] ?? 0)).length;

  /* ── Forecast vs. coverage (stage 8) ─────────────────────────────────
   * Everything here is derived from data the server actually sends:
   * demand slices (hourly) and the day's shifts/breaks. Adherence is NOT
   * derivable — it needs clock-in vs scheduled, which no op serves (REQ-096j),
   * so it renders as unavailable rather than as a computed-looking number. */
  const demandTotals = useMemo(() => demandSlices.map((s) => s.events + s.appointments + s.queue), [dayDemand]);
  const peak = useMemo(() => {
    let bi = -1, bv = 0;
    demandTotals.forEach((v, i) => { if (v > bv) { bv = v; bi = i; } });
    return bi < 0 ? null : { value: bv, at: minToHm(win.startHour * 60 + bi * demandStepMin) };
  }, [demandTotals, win.startHour, demandStepMin]);
  const dayTitle = useMemo(() => {
    const d = new Date(`${activeDay}T00:00:00`);
    return Number.isNaN(d.getTime()) ? activeDay
      : d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [activeDay]);
  const chart = useMemo(() => {
    const n = Math.max(1, demandTotals.length);
    const w = Math.max(320, demandHours * 56), h = 96, step = w / n;
    const y = (v: number) => h - (v / demandMax) * (h - 10) - 2;
    const pts = demandTotals.map((v, i) => `${(i * step + step / 2).toFixed(1)},${y(v).toFixed(1)}`);
    const area = pts.length ? `M ${(step / 2).toFixed(1)},${h} L ${pts.join(" L ")} L ${((demandTotals.length - 1) * step + step / 2).toFixed(1)},${h} Z` : "";
    const cap: string[] = [];
    scheduled.forEach((v, i) => { cap.push(`${(i * step).toFixed(1)},${y(v).toFixed(1)}`, `${((i + 1) * step).toFixed(1)},${y(v).toFixed(1)}`); });
    const gaps = demandTotals.map((v, i) => (v > (scheduled[i] ?? 0) ? i : -1)).filter((i) => i >= 0);
    // One label per hour, wherever the slice grid lands on the hour.
    const ticks = demandTotals.map((_, i) => ((i * demandStepMin) % 60 === 0
      ? { i, label: pad2(win.startHour + (i * demandStepMin) / 60) } : null))
      .filter((x): x is { i: number; label: string } => !!x);
    return { w, h, step, area, line: pts.join(" "), cap: cap.join(" "), gaps, ticks, y };
  }, [demandTotals, scheduled, demandMax, demandHours, demandStepMin, win.startHour]);

  /* ── Coverage per line of business (stage 8) ──────────────────────────
   * A banker skilled in one LOB can't absorb demand in another, so coverage
   * is read per LOB. The ops board carries no skills and shifts carry no LOB
   * (REQ-096g), so an LOB's advisor set is the staff who actually hold one of
   * its bookings today — demonstrated, not assumed. Cover at a moment counts
   * those advisors whose shift covers it and whose breaks don't, which is why
   * a sole banker on lunch reads as a real gap. */
  const lobs = useMemo(() => {
    const byService = new Map<string, OpsAppointment[]>();
    dayAppts.forEach((a) => { const l = byService.get(a.service) ?? []; l.push(a); byService.set(a.service, l); });
    const shiftOf = (name: string) => dayShifts.find((s) => s.staffName === name);
    return [...byService.entries()].map(([service, appts]) => {
      const advisors = [...new Set(appts.map((a) => a.staffName).filter((n): n is string => !!n))];
      const heads = advisors.filter((n) => shiftOf(n)).length;
      let peakN = 0, peakAt = "", peakAvail = 0, firstGapAt = "", gapSlots = 0;
      for (let m = win.startHour * 60; m < win.endHour * 60; m += slotMinutes) {
        const booked = appts.filter((a) => hmToMin(a.start) <= m && hmToMin(a.end) > m).length;
        if (booked === 0) continue;
        const avail = advisors.filter((n) => {
          const sh = shiftOf(n);
          if (!sh || hmToMin(sh.start) > m || hmToMin(sh.end) <= m) return false;
          return !(sh.breaks ?? []).some((b) => hmToMin(b.start) <= m && hmToMin(b.end) > m);
        }).length;
        if (booked > peakN) { peakN = booked; peakAt = minToHm(m); peakAvail = avail; }
        if (booked > avail) { gapSlots += 1; if (!firstGapAt) firstGapAt = minToHm(m); }
      }
      /* Tightness is judged at the busiest moment only. Judging it at ANY slot
       * called a whole LOB tight because one early booking met one early
       * advisor, while the headline counted three on shift — the tile
       * contradicted itself. */
      const verdict: "gap" | "tight" | "covered" = gapSlots > 0 ? "gap" : peakN > 0 && peakAvail === peakN ? "tight" : "covered";
      return { service, count: appts.length, heads, peakN, peakAt, peakAvail, firstGapAt, verdict };
    }).sort((a, b) => b.count - a.count);
  }, [dayAppts, dayShifts, win.startHour, win.endHour, slotMinutes]);
  const lobsShort = lobs.filter((l) => l.verdict !== "covered").length;

  const dayIsShort = (d: string): boolean => {
    if (!week) return false;
    const dem = week.demand?.find((x) => x.date === d);
    if (!dem) return false;
    const step = dem.slices.length ? Math.max(5, Math.round((demandHours * 60) / dem.slices.length)) : 60;
    const sch = scheduledPerSlot(week.shifts.filter((s) => s.date === d), d, win.startHour, dem.slices.length || demandHours, step);
    return dem.slices.some((s, i) => s.events + s.appointments + s.queue > (sch[i] ?? 0));
  };

  const publish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      await publishScheduleWeek(weekStart, locationId ?? undefined);
      toast({ tone: "success", title: t("workforce.schedule.schedulePublished", "Schedule published"), subtitle: i18nT("workforce.schedule.staffCanNowSeeWeekdatesWeekstartLengthDaysOfShifts.icu", { value0: weekDates(weekStart).length }) });
      reload();
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.schedule.publishFailed", "Publish failed"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setPublishing(false); }
  };

  const blankShift = (date: string, start: string, end: string): WfShift => ({
    id: "", staffId: null, staffName: null, role: "", locationId: week?.locationId ?? locationId ?? "",
    date, start, end, breaks: [], source: "manual", status: "draft", ruleFlags: [],
  });
  // ui-local (drawer open; persistence happens on Save via createShift)
  const openNewShift = () => setDrawer({ isNew: true, shift: blankShift(activeDay, `${pad2(win.startHour + 1)}:00`, `${pad2(win.startHour + 9)}:00`) });

  /* ── Drag across the lanes to create (guide step 4) ──────────────── */
  const slotToHm = (slot: number) => minToHm(gridWin.startHour * 60 + Math.min(slots, Math.max(0, slot)) * slotMinutes);
  const laneHit = (e: React.PointerEvent) => {
    const el = rowsRef.current, staffCount = visibleStaff.length;
    if (!el || !staffCount) return null;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left - STAFF_W, y = e.clientY - r.top;
    const slot = Math.floor(x / slotW), row = Math.floor(y / rowH);
    if (x < 0 || slot < 0 || slot >= slots || row < 0 || row >= staffCount) return null;
    return { slot, row };
  };
  const onLaneDown = (e: React.PointerEvent) => {
    if (!canWrite || (e.target as HTMLElement).closest("button")) return;
    const hit = laneHit(e);
    if (!hit) return;
    dragRef.current = { aSlot: hit.slot, bSlot: hit.slot, aRow: hit.row, bRow: hit.row };
    setDrag(dragRef.current);
  };
  const onLaneMove = (e: React.PointerEvent) => {
    const hit = laneHit(e);
    if (!dragRef.current) { setHoverRow(hit ? hit.row : null); return; }
    if (hit) {
      dragRef.current = { ...dragRef.current, bSlot: hit.slot, bRow: hit.row };
      setDrag(dragRef.current);
    }
  };
  const endDrag = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !week) { setDrag(null); return; }
    const lo = Math.min(d.aSlot, d.bSlot), hi = Math.max(d.aSlot, d.bSlot);
    const loRow = Math.min(d.aRow, d.bRow), hiRow = Math.max(d.aRow, d.bRow);
    const ids = visibleStaff.slice(loRow, hiRow + 1).map((s) => s.id);
    const spanning = hi - lo >= 2;
    // Below the drag threshold this reads as a click: one staff member, ~8.5h from here.
    const endSlot = spanning ? hi + 1 : Math.min(slots, lo + Math.round(510 / slotMinutes));
    setDrag(null);
    setHoverRow(null);
    setDrawer({ isNew: true, shift: blankShift(activeDay, slotToHm(lo), slotToHm(endSlot)), staffIds: spanning ? ids : ids.slice(0, 1) });
  };

  const dragOverlay = (() => {
    if (!drag) return null;
    const lo = Math.min(drag.aSlot, drag.bSlot), hi = Math.max(drag.aSlot, drag.bSlot);
    const loRow = Math.min(drag.aRow, drag.bRow), hiRow = Math.max(drag.aRow, drag.bRow);
    const count = hiRow - loRow + 1;
    return (
      <div aria-hidden style={{ position: "absolute", left: STAFF_W + lo * slotW, top: loRow * rowH, width: (hi - lo + 1) * slotW, height: count * rowH, border: `1.5px dashed ${T.p1}`, background: alpha(T.p1, 10), borderRadius: 4, pointerEvents: "none", zIndex: 2, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.white, background: T.p1, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>
          {hmRangeLabel(slotToHm(lo), slotToHm(hi + 1))} · {count} {t("workforce.schedule.staffLower", "staff")}
        </span>
      </div>
    );
  })();

  // Seed the horizontal scroll to 08:00 once the week lands (port step 2).
  useEffect(() => {
    if (!week || seededScroll.current || !gridRef.current) return;
    gridRef.current.scrollLeft = Math.max(0, ((SCROLL_SEED_HOUR - gridWin.startHour) * 60 / slotMinutes) * slotW);
    seededScroll.current = true;
  }, [week, gridWin.startHour, slotMinutes, slotW]);

  /* Scroll affordance (operator, 2026-07-30: "I do not see the scrolling").
   * The lanes have always scrolled, but overlay scrollbars stay hidden until a
   * scroll is already under way and a trackpad's sideways gesture is not an
   * obvious offer on a grid this wide — so the sticky header cell carries
   * explicit hour-paging buttons, disabled at each end so the ends are legible. */
  const [scrollX, setScrollX] = useState(0);
  const [maxScrollX, setMaxScrollX] = useState(0);
  const measureScroll = () => {
    const el = gridRef.current;
    if (!el) return;
    setScrollX(el.scrollLeft);
    setMaxScrollX(Math.max(0, el.scrollWidth - el.clientWidth));
  };
  useEffect(() => {
    measureScroll();
    const onResize = () => measureScroll();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [week, gridW, rowH, density]);
  const scrollByHours = (dir: -1 | 1) => {
    gridRef.current?.scrollBy({ left: dir * 2 * slotsPerHour * slotW, behavior: "smooth" });
  };
  const atStart = scrollX <= 1, atEnd = scrollX >= maxScrollX - 1;
  const pagerBtn = (off: boolean): CSSProperties => ({
    width: 22, height: 22, borderRadius: 5, border: `1px solid ${T.border}`, background: T.white,
    color: off ? T.n4 : T.n2, cursor: off ? "default" : "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0,
  });

  const serviceCounts = useMemo(() => {
    const m = new Map<string, number>();
    (board?.appointments ?? []).forEach((a) => m.set(a.service, (m.get(a.service) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [board]);

  /** Roster rows after the service filter and the sort menu. */
  const visibleStaff = useMemo(() => {
    const apptsFor = (st: WfStaffLite) => apptsByStaffName.get(st.name) ?? [];
    const list = (week?.staff ?? []).filter((st) => !serviceFilter || apptsFor(st).some((a) => a.service === serviceFilter));
    const out = [...list];
    if (sort === "name") out.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "role") out.sort((a, b) => (a.role ?? "").localeCompare(b.role ?? "") || a.name.localeCompare(b.name));
    else if (sort === "skills") out.sort((a, b) => (b.skills?.length ?? 0) - (a.skills?.length ?? 0));
    else if (sort === "booked") out.sort((a, b) => apptsFor(b).length - apptsFor(a).length);
    return out;
  }, [week, serviceFilter, sort, apptsByStaffName]);

  const draftCount = (week?.shifts ?? []).filter((s) => s.status !== "published").length;

  /* Now marker (stage 9): only for today, only inside the window. Ticks each
   * minute so a screen left open doesn't lie about where "now" is. */
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  useEffect(() => {
    const id = setInterval(() => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 60000);
    return () => clearInterval(id);
  }, []);
  const localToday = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }, [nowMin]);
  const nowX = activeDay === localToday && nowMin >= gridWin.startHour * 60 && nowMin <= gridWin.endHour * 60
    ? ((nowMin - gridWin.startHour * 60) / slotMinutes) * slotW : null;

  const timeHeader = (
    <div style={{ display: "flex", height: 34, borderBottom: `1px solid ${T.border}`, background: T.canvas, position: "sticky", top: 0, zIndex: 7 }}>
      <div style={{ width: STAFF_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 6, background: T.canvas, borderRight: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, padding: "0 10px 0 14px", fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {week ? `${new Set(dayShifts.filter((s) => s.staffId).map((s) => s.staffId)).size} ${t("workforce.schedule.onShiftToday", "on shift")}`.toUpperCase() : t('workforce.schedule.text2')}
        </span>
        <span style={{ display: "flex", gap: 4 }}>
          <button type="button" data-testid="wfschedule-scroll-earlier" aria-label={t("workforce.schedule.earlierHours", "Earlier hours")} title={t("workforce.schedule.earlierHours", "Earlier hours")}
            disabled={atStart} onClick={() => scrollByHours(-1)} style={pagerBtn(atStart)}><Icon.arrow dir="left" size={12} /></button>
          <button type="button" data-testid="wfschedule-scroll-later" aria-label={t("workforce.schedule.laterHours", "Later hours")} title={t("workforce.schedule.laterHours", "Later hours")}
            disabled={atEnd} onClick={() => scrollByHours(1)} style={pagerBtn(atEnd)}><Icon.arrow dir="right" size={12} /></button>
        </span>
      </div>
      <div style={{ position: "relative", width: gridW, flexShrink: 0 }}>
        {Array.from({ length: hours }, (_, i) => (
          <span key={i} style={{ position: "absolute", left: i * slotsPerHour * slotW, top: 0, bottom: 0, display: "flex", alignItems: "center", paddingLeft: 6, fontSize: 10.5, color: T.soft, fontFamily: T.fontMono, borderLeft: `1px solid ${T.border}` }}>
            {hmLabel(`${pad2(gridWin.startHour + i)}:00`)}
          </span>
        ))}
        {/* Sub-hour ticks: full at the half hour, short at the quarter. */}
        {Array.from({ length: slots }, (_, j) => {
          const min = (j % slotsPerHour) * slotMinutes;
          if (min === 0) return null;
          const half = min === 30;
          return <span key={`tick-${j}`} aria-hidden style={{ position: "absolute", left: j * slotW, bottom: 0, width: 1, height: half ? 11 : 5, background: T.n6 }} />;
        })}
        {nowX !== null ? (
          <span data-testid="wfschedule-now-chip" style={{ position: "absolute", left: Math.max(0, nowX - 26), bottom: 3, display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: 4, background: T.p1, color: T.white, fontSize: 9.5, fontWeight: 700, fontFamily: T.fontMono, letterSpacing: ".04em", zIndex: 2, pointerEvents: "none" }}>
            {t("workforce.schedule.now", "NOW")} · {minToHm(nowMin)}
          </span>
        ) : null}
      </div>
    </div>
  );

  const shiftBlock = (s: WfShift, compact = false) => {
    const left = ((hmToMin(s.start) - gridWin.startHour * 60) / slotMinutes) * slotW;
    const width = ((hmToMin(s.end) - hmToMin(s.start)) / slotMinutes) * slotW;
    const hue = roleHue(s.role);
    const draft = s.status !== "published";
    // With appointments in the lane the shift keeps the top band, so the two
    // layers never overlap (the appointment fill would otherwise let the
    // shift's own time label bleed through it).
    const blockH = compact ? Math.max(18, Math.round(rowH * 0.5) - 6) : rowH - 12;
    return (
      <button key={s.id} data-testid="wfschedule-shift-block" data-id={s.id} onClick={() => canWrite && setDrawer({ shift: s, isNew: false })}
        title={t("workforce.schedule.sRoleHmrangelabelSStartSEndSEventidEventShiftDraft.icu", { role: s.role, time: hmRangeLabel(s.start, s.end), event: s.eventId ? "yes" : "no", status: draft ? "draft" : "published" })}
        style={{ position: "absolute", left: left + 2, width: Math.max(slotW, width) - 4, top: compact ? 4 : 6, height: blockH, borderRadius: 5, cursor: canWrite ? "pointer" : "default", textAlign: "left", fontFamily: T.font, overflow: "hidden", padding: compact ? "0 8px" : "3px 8px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, background: alpha(hue, 14), border: `1px ${draft ? "dashed" : "solid"} ${alpha(hue, 45)}` }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: hue, flexShrink: 0 }} />
          {s.role || t("workforce.myDay.shift", "Shift")}{" "}{s.eventId ? <Icon.ticket size={10} /> : null}
          {s.ruleFlags.length ? <Icon.warn size={10} /> : null}
        </span>
        {width > 70 && !compact ? <span style={{ fontSize: 10, color: T.n2, whiteSpace: "nowrap" }}>{hmRangeLabel(s.start, s.end)}</span> : null}
        {s.breaks.map((b, i) => {
          const bl = ((hmToMin(b.start) - hmToMin(s.start)) / slotMinutes) * slotW;
          const bw = ((hmToMin(b.end) - hmToMin(b.start)) / slotMinutes) * slotW;
          return <span key={i} aria-hidden style={{ position: "absolute", top: 0, bottom: 0, left: bl, width: bw, background: `repeating-linear-gradient(45deg, transparent 0 4px, ${alpha(T.n3, 20)} 4px 8px)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8.5, fontWeight: 700, letterSpacing: ".06em", color: T.soft, overflow: "hidden" }}>{bw > 46 ? t("workforce.schedule.breakBand", "BREAK") : ""}</span>;
        })}
      </button>
    );
  };

  /** Booked appointments, drawn in the lane's lower band (never over a shift label). */
  const apptBlock = (a: OpsAppointment, outside = false, dim = false) => {
    const left = ((hmToMin(a.start) - gridWin.startHour * 60) / slotMinutes) * slotW;
    const width = ((hmToMin(a.end) - hmToMin(a.start)) / slotMinutes) * slotW;
    const hue = roleHue(a.service);
    const settled = a.status === "done" || a.status === "no-show";
    const h = Math.max(16, Math.round(rowH * 0.42));
    return (
      <button key={a.id} data-testid="wfschedule-appt-block" data-id={a.id}
        onClick={(e) => { e.stopPropagation(); setAppt(a); }}
        title={`${a.service} · ${hmRangeLabel(a.start, a.end)}${a.customer ? ` · ${a.customer}` : ""}${outside ? ` · ${t("workforce.schedule.outsideScheduledShift", "outside the scheduled shift")}` : ""}`}
        style={{ position: "absolute", zIndex: 1, left: left + 2, width: Math.max(slotW, width) - 4, bottom: 4, height: h, borderRadius: 4, cursor: "pointer", textAlign: "left", fontFamily: T.font, overflow: "hidden", padding: "0 6px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, background: `linear-gradient(${alpha(hue, 13)}, ${alpha(hue, 13)}), ${T.white}`, border: `1px ${outside ? "dashed" : "solid"} ${alpha(outside ? T.orange1 : hue, 40)}`, opacity: dim ? 0.18 : settled ? 0.55 : 1, transition: "opacity 200ms" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: hue, flexShrink: 0 }} />
          {a.service}
        </span>
        {rowH >= 62 && a.customer ? <span style={{ fontSize: 9.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.customer}</span> : null}
      </button>
    );
  };

  /* One scroller instead of three. With the detail panel closed the grid is the
   * screen's main surface: it fills the remaining height and owns both axes, so
   * the time header, the demand ribbon and the staff column stay pinned while
   * you read across the day. With the panel open the page scrolls instead and
   * the grid keeps a readable window. */
  const gridFills = mode === "day" && !isMobile && !details;

  const grid = week && (
    <div ref={gridRef} data-testid="wfschedule-grid" onScroll={measureScroll} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "auto", scrollbarWidth: "thin", flex: gridFills ? 1 : undefined, minHeight: gridFills ? 0 : undefined, maxHeight: gridFills ? undefined : "60vh" }}>
      {timeHeader}
      {/* Demand ribbon — pinned under the time header so the forecast stays
          readable against whichever staff rows are in view. */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 34, zIndex: 6, background: T.white }}>
        <div style={{ width: STAFF_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 5, background: T.white, borderRight: `1px solid ${T.border}`, padding: "8px 14px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.n1 }}>{t("workforce.schedule.demand", "Demand")}</span>
          <span style={{ fontSize: 11, color: T.soft }}>{dayDemand ? /* TODO(i18n): replace template-literal with t('workforce.schedule.text3', { ... }) */`${shortSlots > 0 ? `${shortSlots} slots short` : "covered all day"}` : t('workforce.schedule.text4')}</span>
        </div>
        <div style={{ display: "flex", width: gridW, flexShrink: 0 }}>
          {dayDemand
            ? <>
                {/* The served demand window can start later than the grid does; the
                    spacer keeps each hourly stack under its own hour. */}
                {demandLeadHours > 0 ? <span aria-hidden style={{ width: demandLeadHours * slotsPerHour * slotW, flexShrink: 0 }} /> : null}
                {dayDemand.slices.slice(0, demandHours).map((sl, i) => <DemandStack key={sl.t} slice={sl} max={demandMax} scheduled={scheduled[i] ?? 0} width={slotsPerHour * slotW} />)}
              </>
            : <span style={{ fontSize: 11.5, color: T.soft, padding: "14px 12px" }}>{t("workforce.schedule.noDemandDataForThisDay", "No demand data for this day.")}</span>}
        </div>
      </div>
      {/* Open shifts lane */}
      {openShifts.length > 0 ? (
        <div style={{ display: "flex", borderBottom: `1px solid ${T.n6}`, background: alpha(T.orange1, 4) }}>
          <div style={{ width: STAFF_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 5, background: T.white, borderRight: `1px solid ${T.border}`, padding: "0 14px", height: rowH, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 30, height: 30, borderRadius: "50%", border: `1.5px dashed ${T.orange1}`, color: T.orange1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.plus size={13} /></span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t("workforce.schedule.openShifts", "Open shifts")}</div>
              <div style={{ fontSize: 11, color: T.soft }}>{openShifts.length} {t("workforce.schedule.unclaimed", "unclaimed")}</div>
            </div>
          </div>
          <div style={{ position: "relative", width: gridW, height: rowH, flexShrink: 0 }}>{openShifts.map((shift) => shiftBlock(shift))}</div>
        </div>
      ) : null}
      {/* Staff rows */}
      {visibleStaff.length === 0 ? (
        <EmptyState icon={<Icon.users size={20} />}
          title={serviceFilter ? t("workforce.schedule.noStaffMatchThisFilter", "No staff match this filter") : t("workforce.schedule.noStaffAtThisLocation", "No staff at this location")}
          body={serviceFilter ? t("workforce.schedule.noStaffTookThisService", "Nobody has a booking for this service on the selected day.") : t("workforce.schedule.addStaffMembersToThisLocationToStartScheduling", "Add staff members to this location to start scheduling.")} />
      ) : (
        <div ref={rowsRef} style={{ position: "relative" }}
          onPointerDown={onLaneDown} onPointerMove={onLaneMove} onPointerUp={endDrag}
          onPointerCancel={endDrag} onPointerLeave={() => { setHoverRow(null); endDrag(); }}>
        {visibleStaff.map((st, idx) => {
        const laneAppts = hideAppts ? [] : apptsByStaffName.get(st.name) ?? [];
        return (
        <div key={st.id} data-testid="wfschedule-staff-row" data-id={st.id} style={{ display: "flex", borderBottom: idx === visibleStaff.length - 1 ? "none" : `1px solid ${T.n6}` }}>
          <div style={{ width: STAFF_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 5, background: T.white, borderRight: `1px solid ${T.border}`, padding: "0 14px", height: rowH, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: "50%", background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{st.initials}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.name}</div>
              <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.role || "—"} · {st.hoursScheduled}/{st.hoursTarget}{t("workforce.schedule.h", "h")}</div>
            </div>
            {st.hoursScheduled > st.hoursTarget ? <span title={t("workforce.schedule.overHoursTarget", "Over hours target")} style={{ color: T.orange1, display: "inline-flex" }}><Icon.warn size={13} /></span> : null}
          </div>
          <div style={{ position: "relative", width: gridW, height: rowH, flexShrink: 0, background: T.white }}>
            {Array.from({ length: hours }, (_, i) => (
              <span key={i} aria-hidden style={{ position: "absolute", left: i * slotsPerHour * slotW, top: 0, bottom: 0, width: 1, background: T.n6 }} />
            ))}
            {dayShifts.filter((s) => s.staffId === st.id).map((s) => shiftBlock(s, laneAppts.length > 0))}
            {laneAppts.map((a) => apptBlock(a, !dayShifts.some((s) => s.staffId === st.id && hmToMin(s.start) <= hmToMin(a.start) && hmToMin(s.end) >= hmToMin(a.end)), !!serviceFilter && a.service !== serviceFilter))}
            {canWrite && !drag && hoverRow === idx && !dayShifts.some((s) => s.staffId === st.id) ? (
              <span aria-hidden style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 600, color: T.p1, background: alpha(T.p1, 4), pointerEvents: "none" }}>
                {t("workforce.schedule.clickOrDragToAddAShift", "Click or drag to add a shift")}
              </span>
            ) : null}
          </div>
        </div>
        );
        })}
        {dragOverlay}
        {nowX !== null ? (
          <span data-testid="wfschedule-now-line" aria-hidden style={{ position: "absolute", top: 0, bottom: 0, left: STAFF_W + nowX, width: 2, background: T.p1, opacity: 0.55, zIndex: 4, pointerEvents: "none" }} />
        ) : null}
        </div>
      )}
    </div>
  );

  const publishBar = week && (
    <div data-testid="wfschedule-publish-bar" style={{ marginTop: 12, flexShrink: 0, background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: 14, display: "flex", flexDirection: "column", gap: 10, position: isMobile ? "sticky" : "static", bottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: week.status === "published" ? T.green1 : T.orange1, flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>
            {week.status === "published"
              ? `${t("workforce.schedule.published", "Published")} · v${week.version}`
              : `${t("common.status.draft", "Draft")} · ${draftCount} ${draftCount === 1 ? t("workforce.schedule.changeSinceLastPublish", "change since last publish") : t("workforce.schedule.changesSinceLastPublish", "changes since last publish")}`}
          </div>
          <div style={{ fontSize: 11.5, color: T.soft }}>
            {week.publishedAt
              ? `${t("workforce.schedule.lastPublished", "Last published")} ${dayLabel(week.publishedAt.slice(0, 10), "full")}`
              : t("workforce.schedule.neverPublished", "This week has never been published")}
          </div>
        </div>
        <span style={{ marginLeft: "auto" }} />
        {canDiscard && draftCount > 0 ? (
          <Btn variant="secondary" testid="wfschedule-discard" disabled={busy !== null}
            onClick={() => { void run("discard", async () => {
              try {
                await discardScheduleDraft(weekStart, locationId ?? undefined);
                toast({ tone: "success", title: t("workforce.schedule.draftDiscarded", "Draft changes discarded") });
                reload();
              } catch (e) {
                toast({ tone: "danger", title: t("workforce.schedule.discardUnavailable", "Discarding isn't available"), subtitle: reasonOf(e) });
              }
            }); }}>
            {busy === "discard" ? t("workforce.schedule.discarding", "Discarding…") : t("workforce.schedule.discardChanges", "Discard changes")}
          </Btn>
        ) : null}
        {canPublish ? (
          <Btn variant="secondary" testid="wfschedule-preview-impact" disabled={busy !== null}
            onClick={() => { void run("checks", async () => {
              try {
                const res = await previewPublishImpact(weekStart, locationId ?? undefined);
                if (res && typeof res.draftChanges === "number") { setChecks(res); setChecksError(null); }
                else {
                  setChecks(null);
                  const why = t("workforce.schedule.impactNoNumbers", "the service answered without any figures");
                  setChecksError(why);
                  toast({ tone: "warn", title: t("workforce.schedule.impactUnavailable", "Pre-publish checks aren't available"), subtitle: `previewPublishImpact · ${why}` });
                }
              } catch (e) {
                setChecks(null);
                // An empty-message rejection is common here (the request is
                // refused with no body). Never store "" — a falsy reason would
                // read as "no error" and hide the notice entirely.
                const why = reasonOf(e);
                setChecksError(why);
                toast({ tone: "danger", title: t("workforce.schedule.impactUnavailable", "Pre-publish checks aren't available"), subtitle: `previewPublishImpact · ${why}` });
              }
            }); }}>
            {busy === "checks" ? t("workforce.schedule.checking", "Checking…") : t("workforce.schedule.previewImpact", "Preview impact")}
          </Btn>
        ) : null}
        {canPublish ? (
          <Btn icon={<Icon.check size={14} />} testid="wfschedule-publish-action" disabled={publishing || !week} onClick={publish}>
            {publishing ? t("workforce.workforceSchedule.publishing", "Publishing…") : t("workforce.workforceSchedule.publishWeek", "Publish week")}
          </Btn>
        ) : null}
      </div>
      {checks ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 10, borderTop: `1px solid ${T.n6}` }} data-testid="wfschedule-prepublish-checks">
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {([[t("workforce.schedule.draftChanges", "Draft changes"), String(checks.draftChanges)],
               [t("workforce.schedule.budgetHours", "Budget hours"), String(checks.budgetHours)],
               [t("workforce.schedule.projectedOvertime", "Projected overtime"), `${checks.projectedOtHours}h`],
               [t("workforce.schedule.advanceNotice", "Advance notice"), `${checks.noticeDays}/${checks.noticeMinimumDays} ${t("workforce.schedule.days", "days")}`]] as [string, string][]).map(([k, v]) => (
              <span key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft, fontWeight: 700 }}>{k}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{v}</span>
              </span>
            ))}
          </div>
          {(checks.warnings ?? []).map((w, i) => <span key={i} style={{ fontSize: 11.5, color: T.orange1 }}>{w}</span>)}
          <span style={{ fontSize: 10.5, color: T.soft }}>{t("workforce.schedule.checkedAgainstRules", "Checked against Rules & policies")}{" "}{checks.rulesVersion}</span>
        </div>
      ) : checksError !== null ? (
        <div style={{ paddingTop: 10, borderTop: `1px solid ${T.n6}`, display: "flex", flexDirection: "column", gap: 2 }} data-testid="wfschedule-prepublish-unavailable">
          <span style={{ fontSize: 12, fontWeight: 600, color: T.n1 }}>{t("workforce.schedule.impactUnavailable", "Pre-publish checks aren't available")}</span>
          <span style={{ fontSize: 11.5, color: T.n2 }}>{t("workforce.schedule.impactUnavailableBody", "Nothing has been calculated, so no figures are shown. Publishing still works.")}</span>
          <span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono }}>previewPublishImpact · {checksError}</span>
        </div>
      ) : null}
    </div>
  );

  const mobileList = week && (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {dayShifts.length === 0 ? <EmptyState icon={<Icon.calendar size={20} />} title={t("workforce.schedule.noShiftsThisDay", "No shifts this day")} body={t("workforce.schedule.nothingScheduledForTheSelectedDay", "Nothing scheduled for the selected day.")} /> : null}
      {dayShifts.map((s) => {
        const inShift = (s.staffName ? apptsByStaffName.get(s.staffName) ?? [] : []).filter((a) => hmToMin(a.start) < hmToMin(s.end) && hmToMin(a.end) > hmToMin(s.start));
        return (
          <div key={s.id} style={{ borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowSm, overflow: "hidden" }}>
            <button data-testid="wfschedule-shift-block" data-id={s.id} onClick={() => canWrite && setDrawer({ shift: s, isNew: false })}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", width: "100%", minHeight: 44, border: "none", background: "transparent", textAlign: "left", cursor: canWrite ? "pointer" : "default", fontFamily: T.font }}>
              <span style={{ width: 8, height: 34, borderRadius: 4, background: roleHue(s.role), flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{s.staffName || t("workforce.workforceSchedule.openShift", "Open shift")} · {s.role}</span>
                <span style={{ display: "block", fontSize: 12, color: T.soft }}>{hmRangeLabel(s.start, s.end)}{s.eventId ? t("workforce.workforceSchedule.eventShift", " · event shift") : ""}</span>
              </span>
              {s.status !== "published" ? <Pill tone="warn">{t("common.status.draft", "Draft")}</Pill> : null}
            </button>
            {inShift.length > 0 ? (
              <div style={{ borderTop: `1px solid ${T.n6}`, background: alpha(T.p1, 3) }}>
                {inShift.map((a) => (
                  <button key={a.id} data-testid="wfschedule-appt-row" data-id={a.id} onClick={() => setAppt(a)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px 10px 34px", width: "100%", minHeight: 44, border: "none", borderTop: `1px solid ${alpha(T.n3, 12)}`, background: "transparent", textAlign: "left", cursor: "pointer", fontFamily: T.font }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: roleHue(a.service), flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.service}{a.customer ? ` · ${a.customer}` : ""}</span>
                      <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{hmRangeLabel(a.start, a.end)}</span>
                    </span>
                    <Icon.arrow size={14} dir="right" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  /* Day picker. It lives in the page header beside the date title (desktop):
   * it is the control that changes that title, and folding it up there buys the
   * grid back the ~42px row it used to occupy under the toolbar. Mobile keeps
   * the standalone strip — the header has no room for seven segments. */
  const weekStrip = (
    <div data-testid="wfschedule-day-strip" style={{ display: "inline-flex", borderRadius: 999, border: `1px solid ${T.border}`, overflow: "hidden", background: T.white }}>
      {days.map((d, i) => {
        const active = d === activeDay;
        const short = dayIsShort(d);
        return (
          <button key={d} data-testid="wfschedule-day-tab" data-id={d} onClick={() => setDay(d)} title={dayLabel(d, "full")}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", minHeight: isMobile ? 44 : 32, border: "none", borderLeft: i === 0 ? "none" : `1px solid ${T.border}`, background: active ? T.p1 : T.white, color: active ? T.white : T.n2, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer", whiteSpace: "nowrap" }}>
            {dayLabel(d, "wk")}
            <span style={{ fontFamily: T.fontMono, fontSize: 11, color: active ? alpha(T.white, 70) : T.soft }}>{parseInt(d.slice(8), 10)}</span>
            {short ? <span aria-label={t("workforce.schedule.underStaffed", "under-staffed")} style={{ width: 5, height: 5, borderRadius: "50%", background: active ? T.white : T.red1 }} /> : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <Screen rail="wf-schedule">
      <PageHeader
        title={dayTitle}
        breadcrumb={null}
        actions={(
          <>
            {!isNarrow && mode === "day" ? weekStrip : null}
            {week ? (week.status === "published" ? <Pill tone="success" dot>{t("workforce.schedule.publishedV", "Published · v")}{" "}{week.version}</Pill> : week.status === "partial" ? <Pill tone="warn" dot>{t("workforce.schedule.partiallyPublished", "Partially published")}</Pill> : <Pill tone="neutral" dot>{t("common.status.draft", "Draft")}</Pill>) : null}
          </>
        )}
      />
      <DataSourceStrip screen="wfschedule" ops={["getScheduleWeek", "getOpsBoard", "listLaborRules", "evaluateShiftRules", "createShift", "updateShift", "deleteShift", "publishScheduleWeek", "previewPublishImpact", "discardScheduleDraft", "syncBookings"]} />
      {/* Forecast vs. coverage (stage 8) — replaces the five stat tiles */}
      {mode === "day" ? (
        <div data-testid="wfschedule-forecast-card" style={{ margin: "16px 24px 0", flexShrink: 0, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowSm, overflow: "hidden" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: isMobile ? 16 : 28, padding: "14px 18px" }}>
            <div style={{ minWidth: 168 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.soft }}>{t("workforce.schedule.forecastVsCoverage", "Forecast vs. coverage")}</div>
              <div style={{ fontSize: 15.5, fontWeight: 600, color: T.n1, marginTop: 4 }}>{dayTitle}</div>
            </div>
            <ForecastStat label={t("workforce.schedule.booked", "Booked")} value={String(dayAppts.length)} />
            <ForecastStat label={t("workforce.schedule.peakDemand", "Peak demand")} value={peak ? String(peak.value) : "—"} note={peak ? `@ ${hmLabel(peak.at)}` : t("workforce.schedule.noForecastForThisDay", "no forecast for this day")} />
            <ForecastStat label={t("workforce.schedule.coverageGap", "Coverage gap")} value={dayDemand ? String(shortSlots) : "—"} note={dayDemand ? t("workforce.schedule.slotsLower", "slots") : undefined} tone={shortSlots > 0 ? T.red1 : T.green1} />
            <ForecastStat label={t("workforce.schedule.adherence", "Adherence")} value="—" note={t("workforce.schedule.needsClockInData", "needs clock-in data")} />
            <span style={{ marginLeft: "auto" }} />
            {!isMobile ? (
              <div style={{ display: "flex", gap: 14, alignItems: "center", fontSize: 11.5, color: T.soft }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: alpha(T.p1, 28), border: `1px solid ${T.p1}` }} />{t("workforce.schedule.demand", "Demand")}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 2, background: T.n1 }} />{t("workforce.schedule.capacity", "Capacity")}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 3, background: T.red1 }} />{t("workforce.schedule.gap", "Gap")}</span>
              </div>
            ) : null}
            <button type="button" data-testid="wfschedule-details-toggle" aria-expanded={details} onClick={toggleDetails}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", minHeight: 32, borderRadius: 999, border: `1px solid ${T.border}`, background: T.white, color: T.n2, fontSize: 12, fontWeight: 600, fontFamily: T.font, cursor: "pointer", flexShrink: 0 }}>
              {details ? t("workforce.schedule.hideDetail", "Hide detail") : t("workforce.schedule.showDetail", "Show detail")}
              <Icon.arrow dir={details ? "up" : "down"} size={11} />
            </button>
          </div>
          {details ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(200px, 232px) 1fr", borderTop: `1px solid ${T.n6}` }}>
            <div style={{ padding: "14px 18px", borderRight: isMobile ? "none" : `1px solid ${T.n6}`, borderBottom: isMobile ? `1px solid ${T.n6}` : "none" }}>
              {!dayDemand ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".06em" }}>{t("workforce.schedule.noForecast", "No forecast")}</div>
                  <div style={{ fontSize: 12.5, color: T.n2, marginTop: 8, lineHeight: 1.45 }}>{t("workforce.schedule.noForecastBody", "The server sent no demand forecast for this day, so coverage can't be compared. Shifts below are still real.")}</div>
                </>
              ) : shortSlots === 0 ? (
                <>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: T.green1, textTransform: "uppercase", letterSpacing: ".06em" }}><Icon.check size={13} />{t("workforce.schedule.demandCovered", "Demand covered")}</div>
                  <div style={{ fontSize: 12.5, color: T.n2, marginTop: 8, lineHeight: 1.45 }}>{t("workforce.schedule.demandCoveredBody", "Scheduled cover meets or exceeds forecast demand in every hour of the day.")}</div>
                </>
              ) : (
                <>
                  <div data-testid="wfschedule-understaffed" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: T.red1, textTransform: "uppercase", letterSpacing: ".06em" }}>
                    <Icon.warn size={13} />{shortSlots} {shortSlots === 1 ? t("workforce.schedule.understaffedSlot", "understaffed slot") : t("workforce.schedule.understaffedSlots", "understaffed slots")}
                  </div>
                  <div style={{ fontSize: 12.5, color: T.n2, margin: "8px 0 12px", lineHeight: 1.45 }}>
                    {peak ? `${t("workforce.schedule.peakShortfallBody", "Demand exceeds scheduled cover — heaviest at")} ${hmLabel(peak.at)}.` : t("workforce.schedule.shortfallBody", "Demand exceeds scheduled cover.")}
                  </div>
                  {canWrite && peak ? (
                    <Btn variant="secondary" icon={<Icon.plus size={13} />} testid="wfschedule-apply-fix"
                      onClick={() => setDrawer({ isNew: true, shift: blankShift(activeDay, peak.at, minToHm(Math.min(win.endHour * 60, hmToMin(peak.at) + 120))) })}>
                      {t("workforce.schedule.addCoverAtPeak", "Add cover at peak")}
                    </Btn>
                  ) : null}
                </>
              )}
            </div>
            <div style={{ padding: "12px 8px 10px", overflowX: "auto" }}>
              {demandTotals.length ? (
                <svg viewBox={`0 0 ${chart.w} ${chart.h + 16}`} width="100%" height={chart.h + 16} preserveAspectRatio="none" role="img"
                  aria-label={t("workforce.schedule.chartAria", "Forecast demand against scheduled capacity, by hour")}>
                  <path d={chart.area} fill={alpha(T.p1, 16)} stroke={T.p1} strokeWidth={1.5} />
                  <polyline points={chart.cap} fill="none" stroke={T.n1} strokeWidth={1.5} />
                  {chart.gaps.map((i) => (
                    <rect key={i} x={i * chart.step + 2} y={chart.y(demandTotals[i]) - 5} width={chart.step - 4} height={3} fill={T.red1} rx={1.5} />
                  ))}
                  {chart.ticks.map((tk) => (
                    <text key={tk.i} x={tk.i * chart.step + chart.step / 2} y={chart.h + 12} textAnchor="middle" fontSize={9.5} fill={T.soft} fontFamily={T.fontMono}>{tk.label}</text>
                  ))}
                </svg>
              ) : (
                <div style={{ padding: "22px 12px", fontSize: 12.5, color: T.soft }}>{t("workforce.schedule.noDemandSeries", "No demand series for this day.")}</div>
              )}
            </div>
          </div>
          ) : null}
        </div>
      ) : null}
      {/* Coverage per line of business (stage 8) — detail, behind the same toggle */}
      {mode === "day" && details && lobs.length ? (
        <div data-testid="wfschedule-lob-card" style={{ margin: "12px 24px 0", flexShrink: 0, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowSm, padding: "14px 18px 18px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600, color: T.n1 }}>{t("workforce.schedule.coverageByLob", "Coverage by line of business")}</div>
              <div style={{ fontSize: 12.5, color: T.n2, marginTop: 4, lineHeight: 1.45, textWrap: "pretty" }}>
                {dayShifts.filter((s) => s.staffId).length} {t("workforce.schedule.lobExplainer", "advisors on shift — but an advisor skilled in one line of business can't absorb demand in another, so coverage is read per line of business.")}
              </div>
            </div>
            {lobsShort > 0 ? <Pill tone="danger">{lobsShort} {lobsShort === 1 ? t("workforce.schedule.lobShortOne", "LOB short at peak") : t("workforce.schedule.lobShortMany", "LOBs short at peak")}</Pill> : <Pill tone="success">{t("workforce.schedule.allLobsCovered", "All lines covered")}</Pill>}
          </div>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(212px, 1fr))", gap: 10 }}>
            {lobs.map((l) => {
              const tone = l.verdict === "gap" ? T.red1 : l.verdict === "tight" ? T.orange1 : T.green1;
              return (
                <div key={l.service} data-testid="wfschedule-lob-tile" data-id={l.service} style={{ borderRadius: 8, border: `1px solid ${T.border}`, borderLeft: `3px solid ${tone}`, background: T.white, padding: "11px 13px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: roleHue(l.service), flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.service}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: tone }}>
                      {l.verdict === "gap" ? t("workforce.schedule.lobGap", "Gap") : l.verdict === "tight" ? t("workforce.schedule.lobTight", "Tight") : t("workforce.schedule.lobCovered", "Covered")}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 8 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color: T.n1, fontFamily: T.fontMono }}>{l.heads}</span>
                    <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.schedule.onShiftAppts", "on shift")} · {l.count} {t("workforce.schedule.apptsShort", "appts")}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: l.verdict === "gap" ? T.red1 : T.n2, marginTop: 6, lineHeight: 1.4, textWrap: "pretty" }}>
                    {l.verdict === "gap"
                      ? `${t("workforce.schedule.lobGapAt", "Booked at")} ${hmLabel(l.firstGapAt)} ${t("workforce.schedule.lobGapNoCover", "with no advisor available — on break or off shift")}`
                      : l.verdict === "tight"
                        ? `${l.peakAvail} ${t("workforce.schedule.lobOfAvailable", "of")} ${l.heads} ${t("workforce.schedule.lobAvailableAt", "available at")} ${hmLabel(l.peakAt)} — ${t("workforce.schedule.lobNoSlack", "no slack at the busiest moment")}`
                        : `${t("workforce.schedule.lobPeak", "Peak")} ${l.peakN} ${t("workforce.schedule.lobConcurrent", "concurrent — cover to spare")}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <FilterBar>
        <div style={{ display: "inline-flex", borderRadius: 999, border: `1px solid ${T.border}`, overflow: "hidden" }}>
          {([["day", t("workforce.schedule.day", "Day")], ["week", t("workforce.schedule.week", "Week")]] as ["day" | "week", string][]).map(([id, label]) => (
            <button key={id} data-testid="wfschedule-mode-toggle" data-id={id} onClick={() => setModeUrl(id)}
              style={{ padding: "6px 14px", minHeight: 32, border: "none", background: mode === id ? T.p1 : T.white, color: mode === id ? T.white : T.n2, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{label}</button>
          ))}
        </div>
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <LocationFilter value={locationId} onChange={setLocationId} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <WeekNav weekStart={weekStart} onChange={(ws) => { setWeekStart(ws); setDay(ws); }} testid="wfschedule-weeknav" />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {/* TODO(i18n): suggested keys workforce.schedule.density / .densityCompact / .densityComfortable / .densitySpacious */}
        <FilterDropdown
          label={`${t("workforce.schedule.density", "Density")}: ${t(`workforce.schedule.density${density.charAt(0).toUpperCase()}${density.slice(1)}`, DENSITY_LABELS[density])}`}
          active
          options={DENSITY_ORDER.map((d) => DENSITY_LABELS[d])}
          onPick={(label) => setDensity(DENSITY_ORDER.find((d) => DENSITY_LABELS[d] === label) ?? "comfortable")}
          onClear={() => setDensity("comfortable")}
        />
        <span style={{ marginLeft: "auto" }} />
        {!isMobile ? <SourceLegend /> : null}
      </FilterBar>
      {/* Service filter chips · sort · shifts-only · sync (guide step 7) */}
      <div style={{ padding: "0 24px 10px", display: mode === "week" ? "none" : "flex", gap: 8, alignItems: "center", flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible" }}>
        {serviceCounts.length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: isMobile ? "nowrap" : "wrap" }}>
            <button data-testid="wfschedule-service-chip" data-id="__all" onClick={() => setServiceFilter(null)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", minHeight: 32, borderRadius: 999, border: `1px solid ${serviceFilter ? T.border : T.p1}`, background: serviceFilter ? T.white : T.p1, color: serviceFilter ? T.n1 : T.white, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap" }}>
              {t("workforce.schedule.allStaff", "All staff")}{" "}{serviceFilter ? null : <span style={{ color: alpha(T.white, 70), fontFamily: T.fontMono, fontSize: 11 }}>{visibleStaff.length}</span>}
            </button>
            {serviceCounts.map(([svc, n]) => {
              const on = serviceFilter === svc;
              return (
                <button key={svc} data-testid="wfschedule-service-chip" data-id={svc} onClick={() => setServiceFilter(on ? null : svc)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", minHeight: 32, borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: T.n1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: roleHue(svc) }} />
                  {svc}
                  <span style={{ color: T.soft, fontFamily: T.fontMono, fontSize: 11 }}>{n}</span>
                </button>
              );
            })}
          </div>
        ) : null}
        <span style={{ marginLeft: "auto" }} />
        <FilterDropdown label={SORT_LABELS[sort]} active={sort !== "default"} options={(Object.keys(SORT_LABELS) as SortKey[]).map((k) => SORT_LABELS[k])}
          onPick={(v) => {
            const key = (Object.keys(SORT_LABELS) as SortKey[]).find((k) => SORT_LABELS[k] === v) ?? "default";
            setSort(key);
            toast({ tone: "info", title: t("workforce.schedule.sortedBy", "Sorted by") + " " + SORT_LABELS[key].toLowerCase() });
          }}
          onClear={() => setSort("default")} />
        <Btn variant={hideAppts ? "primary" : "secondary"} testid="wfschedule-hide-appts" onClick={() => setHideAppts((v) => !v)}>
          {hideAppts ? t("workforce.schedule.showingShiftsOnly", "Shifts only") : t("workforce.schedule.hideAppointments", "Hide Appointments")}
        </Btn>
        {canWrite ? (
          <Btn variant="secondary" icon={<Icon.refresh size={14} />} testid="wfschedule-sync" disabled={busy !== null}
            onClick={() => { void run("sync", async () => {
              try {
                const res = await syncBookings(locationId ?? undefined);
                toast({ tone: "success", title: t("workforce.schedule.bookingsSynced", "Bookings synced"), subtitle: `${res.added} ${t("workforce.schedule.added", "added")} · ${res.updated} ${t("workforce.schedule.updated", "updated")}` });
                reload();
              } catch (e) {
                toast({ tone: "danger", title: t("workforce.schedule.syncUnavailable", "Booking sync isn't available"), subtitle: reasonOf(e) });
              }
            }); }}>
            {busy === "sync" ? t("workforce.schedule.syncing", "Syncing…") : t("workforce.schedule.sync", "Sync")}
          </Btn>
        ) : null}
        {canWrite ? <Btn icon={<Icon.plus size={14} />} testid="wfschedule-addshift-open" onClick={openNewShift}>{t("workforce.schedule.addShift", "Add shift")}</Btn> : null}
      </div>
      {isNarrow && mode === "day" ? (
        <div style={{ padding: "0 24px 10px", display: "flex", overflowX: "auto" }}>{weekStrip}</div>
      ) : null}
      <div style={{ flex: gridFills ? 1 : "1 0 auto", minHeight: gridFills ? 380 : 0, display: "flex", flexDirection: "column", padding: "0 24px 24px" }}>
        {error ? <WfPending screen="wfschedule" opName="getScheduleWeek" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.schedule.loadingSchedule", "Loading schedule…")}</div>
          : mode === "week" && week ? (
            <WorkforceScheduleWeek week={week} weekStart={weekStart} locationId={locationId} canWrite={canWrite}
              onAddShift={(date, staffId) => setDrawer({ isNew: true, shift: blankShift(date, `${pad2(win.startHour + 1)}:00`, `${pad2(win.startHour + 9)}:00`), staffIds: [staffId] })}
              onReload={reload} />
          )
          : isMobile ? mobileList : grid}
        {!error && !loading ? publishBar : null}
      </div>

      {appt ? <AppointmentDrawer appt={appt} onClose={() => setAppt(null)} onOpenBooking={can("appointments.read") ? () => { setAppt(null); nav.go("appointments"); } : undefined} /> : null}

      {impact ? (
        <RemoveShiftDrawer
          shift={impact.shift}
          appts={impact.appts}
          busyId={busy}
          onClose={() => setImpact(null)}
          onOpenBooking={can("appointments.read") ? () => { setImpact(null); setDrawer(null); nav.go("appointments"); } : undefined}
          onConfirm={async () => {
            await run("delete", async () => {
              try {
                await deleteShift(impact.shift.id);
                toast({ tone: "success", title: t("workforce.schedule.shiftRemoved", "Shift removed") });
                setImpact(null);
                setDrawer(null);
                reload();
              } catch (e) {
                toast({ tone: "danger", title: t("workforce.schedule.couldnTRemoveShift", "Couldn't remove shift"), subtitle: e instanceof Error ? e.message : String(e) });
              }
            });
          }}
        />
      ) : null}

      {drawer ? (
        <ShiftDrawer
          state={drawer}
          week={week}
          days={days}
          timeOptions={timeOptions}
          laborRules={laborRules?.rules ?? []}
          busyId={busy}
          onClose={() => setDrawer(null)}
          onSubmit={async (drafts, isNew) => {
            await run("save", async () => {
              try {
                if (isNew) for (const d of drafts) await createShift(d);
                else await updateShift(drafts[0].id, drafts[0]);
                toast({
                  tone: "success",
                  title: isNew
                    ? `${drafts.length} ${drafts.length === 1 ? t("workforce.schedule.shiftAdded", "shift added") : t("workforce.schedule.shiftsAdded", "shifts added")}`
                    : t("workforce.schedule.shiftUpdated", "Shift updated"),
                });
                setDrawer(null);
                reload();
              } catch (e) {
                toast({ tone: "danger", title: t("workforce.schedule.couldnTSaveShift", "Couldn't save shift"), subtitle: e instanceof Error ? e.message : String(e) });
              }
            });
          }}
          onDelete={drawer.isNew ? undefined : async () => {
            // Never delete straight from the drawer: bookings inside the block
            // have to be reviewed first (guide step 6a).
            const inWindow = (drawer.shift.staffName ? apptsByStaffName.get(drawer.shift.staffName) ?? [] : [])
              .filter((a) => hmToMin(a.start) < hmToMin(drawer.shift.end) && hmToMin(a.end) > hmToMin(drawer.shift.start));
            setImpact({ shift: drawer.shift, appts: inWindow });
          }}
        />
      ) : null}

    </Screen>
  );
}

/**
 * Appointment detail — one record, so a Drawer per the overlay standard
 * (docs/overlay-standard.md), not the prototype's anchored popover: it reads as
 * a pushed screen on phone and rides the URL as ?panel=wfschedule-appointment.
 * Reassign is deliberately absent — no operation exists to move a booking to
 * another staff member (REQ-096); a control that only toasted would be fake.
 */
function AppointmentDrawer({ appt, onClose, onOpenBooking }: {
  appt: OpsAppointment; onClose: () => void; onOpenBooking?: () => void;
}) {
  const t = useT();
  const STATUS_LABEL: Record<OpsAppointment["status"], string> = {
    upcoming: t("workforce.schedule.apptUpcoming", "Upcoming"),
    "checked-in": t("workforce.schedule.apptCheckedIn", "Checked in"),
    "in-progress": t("workforce.schedule.apptInProgress", "In progress"),
    done: t("workforce.schedule.apptDone", "Done"),
    "no-show": t("workforce.schedule.apptNoShow", "No show"),
  };
  const tone = appt.status === "no-show" ? "danger" : appt.status === "done" ? "neutral" : appt.status === "in-progress" ? "warn" : "success";
  const row = (label: string, value: string) => (
    <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${T.n6}` }}>
      <span style={{ width: 96, flexShrink: 0, fontSize: 12, color: T.soft }}>{label}</span>
      <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{value}</span>
    </div>
  );
  return (
    <Drawer panel="wfschedule-appointment" width={420} testid="wfschedule-appt-drawer" icon={<Icon.calendar size={16} />}
      title={appt.customer || appt.service} sub={`${appt.service} · ${hmRangeLabel(appt.start, appt.end)}`} onClose={onClose}
      footer={<>
        <span style={{ flex: 1 }} />
        <Btn variant="secondary" testid="wfschedule-appt-close" onClick={onClose}>{t("common.actions.close", "Close")}</Btn>
        {onOpenBooking ? <Btn testid="wfschedule-appt-open-booking" onClick={onOpenBooking}>{t("workforce.schedule.openBooking", "Open booking")}</Btn> : null}
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: roleHue(appt.service), flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.06, textTransform: "uppercase", color: T.soft }}>{appt.service}</span>
          <span style={{ marginLeft: "auto" }}><Pill tone={tone}>{STATUS_LABEL[appt.status]}</Pill></span>
        </div>
        {row(t("workforce.schedule.time", "Time"), hmRangeLabel(appt.start, appt.end))}
        {row(t("workforce.schedule.advisor", "Advisor"), appt.staffName)}
        {row(t("workforce.schedule.customer", "Customer"), appt.customer || "—")}
        {row(t("common.status.status", "Status"), STATUS_LABEL[appt.status])}
      </div>
    </Drawer>
  );
}

/**
 * Remove-shift impact review (guide step 6a). A shift must never be deleted out
 * from under its bookings, so removal routes through this list of the
 * appointments sitting inside the block's window.
 *
 * The guide asks for per-booking reassignment here. No operation moves an admin
 * appointment between staff (REQ-096c), so instead of a Reassign control that
 * would only toast, each row deep-links to the booking and removal requires an
 * explicit acknowledgement that names what is being left unstaffed.
 *
 * It is a Drawer, not a Modal: the affected-booking list scrolls, and a modal
 * may not scroll (docs/overlay-standard.md).
 */
function RemoveShiftDrawer({ shift, appts, busyId, onClose, onConfirm, onOpenBooking }: {
  shift: WfShift; appts: OpsAppointment[]; busyId: string | null;
  onClose: () => void; onConfirm: () => Promise<void>; onOpenBooking?: () => void;
}) {
  const t = useT();
  const [ack, setAck] = useState(false); // ui-local (acknowledgement gate)
  const blocking = appts.length > 0;
  return (
    <Drawer panel="wfschedule-remove-impact" width={440} testid="wfschedule-remove-drawer" icon={<Icon.warn size={16} />}
      title={t("workforce.schedule.removeThisShift", "Remove this shift?")}
      sub={`${shift.staffName ?? t("workforce.workforceSchedule.openShift", "Open shift")} · ${hmRangeLabel(shift.start, shift.end)} · ${dayLabel(shift.date, "full")}`}
      onClose={onClose}
      footer={<>
        <span style={{ flex: 1 }} />
        <Btn variant="secondary" testid="wfschedule-remove-cancel" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn testid="wfschedule-remove-confirm" disabled={busyId !== null || (blocking && !ack)} onClick={() => { void onConfirm(); }}>
          {busyId === "delete" ? t("workforce.workforceSchedule.removing", "Removing…") : t("workforce.schedule.removeShift", "Remove shift")}
        </Btn>
      </>}>
      {blocking ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ padding: "10px 12px", borderRadius: 6, background: alpha(T.orange1, 8), border: `1px solid ${alpha(T.orange1, 30)}` }}>
            <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1 }}>
              {appts.length} {appts.length === 1 ? t("workforce.schedule.bookingInsideThisShift", "booking sits inside this shift") : t("workforce.schedule.bookingsInsideThisShift", "bookings sit inside this shift")}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: T.n2, marginTop: 2 }}>
              {t("workforce.schedule.removeImpactBody", "They stay booked with the customer, but nobody will be scheduled to take them. Move them to another advisor from the booking itself before removing this shift.")}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflow: "auto" }}>
            {appts.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: roleHue(a.service), flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.service}{a.customer ? ` · ${a.customer}` : ""}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{hmRangeLabel(a.start, a.end)}</span>
                </span>
                {onOpenBooking ? (
                  <button data-testid="wfschedule-remove-openbooking" data-id={a.id} onClick={onOpenBooking}
                    style={{ fontSize: 12, fontWeight: 600, color: T.p1, background: "none", border: "none", cursor: "pointer", fontFamily: T.font, minHeight: 44, padding: "0 4px" }}>
                    {t("workforce.schedule.openBooking", "Open booking")}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <button data-testid="wfschedule-remove-ack" onClick={() => setAck((v) => !v)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", minHeight: 44, border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
            <span style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${ack ? T.p1 : T.n4}`, background: ack ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{ack ? <Icon.check size={10} /> : null}</span>
            <span style={{ fontSize: 12.5, color: T.n1 }}>
              {appts.length === 1
                ? t("workforce.schedule.removeAckOne", "I understand this booking will be left without an advisor on shift")
                : t("workforce.schedule.removeAckMany", "I understand these bookings will be left without an advisor on shift")}
            </span>
          </button>
        </div>
      ) : (
        <span style={{ fontSize: 12.5, color: T.n2 }}>
          {t("workforce.schedule.removeNoBookings", "No bookings fall inside this shift, so nothing else is affected.")}
        </span>
      )}
    </Drawer>
  );
}

/**
 * Shift drawer — the prototype's Add-shift dialog and master's edit drawer, one
 * overlay. Sections in the guide's order: applies-to · date range · times ·
 * compliance · save. On the shared <Drawer> shell per the overlay standard
 * (\?panel=wfschedule-shift, full-screen on phone).
 *
 * Compliance is SERVER-EVALUATED (evaluateShiftRules, REQ-096b). While that op
 * is unserved the panel says so plainly and lists the live rule set from
 * listLaborRules — it never green-lights a draft with client-side arithmetic,
 * and it never blocks saving on a check that did not run.
 */
type Applies = "individual" | "role" | "selected";

function ShiftDrawer({ state, week, days, timeOptions, laborRules, busyId, onClose, onSubmit, onDelete }: {
  state: DrawerState; week: ScheduleWeek | null; days: string[]; timeOptions: string[];
  laborRules: LaborRule[]; busyId: string | null;
  onClose: () => void;
  onSubmit: (drafts: WfShift[], isNew: boolean) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const t = useT();
  const staff = week?.staff ?? [];
  const roles = Array.from(new Set(staff.map((s) => s.role).filter((r): r is string => !!r)));
  const prefill = state.staffIds ?? (state.shift.staffId ? [state.shift.staffId] : []);

  // ui-local (edit buffer; persisted via onSubmit)
  const [applies, setApplies] = useState<Applies>(prefill.length > 1 ? "selected" : "individual");
  const [selected, setSelected] = useState<string[]>(prefill);
  const [roleTarget, setRoleTarget] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(state.shift.date);
  const [dateTo, setDateTo] = useState(state.dateTo ?? state.shift.date);
  const [start, setStart] = useState(state.shift.start);
  const [end, setEnd] = useState(state.shift.end);
  const [role, setRole] = useState(state.shift.role);
  const [ack, setAck] = useState(false);
  const [evaluation, setEvaluation] = useState<ShiftRuleEvaluation | null>(null);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  // A response that carries no checks[] is NOT an evaluation — the preview seed
  // layer answers 404 bodies without rejecting, which would otherwise leave the
  // panel showing a rule list and nothing saying the draft went unchecked.
  const checks = evaluation && Array.isArray(evaluation.checks) ? evaluation.checks : null;
  const unchecked = !evaluating && !checks;

  const targetIds = applies === "role" ? staff.filter((s) => s.role === roleTarget).map((s) => s.id)
    : applies === "selected" ? selected
    : selected.slice(0, 1);
  const dateRange = (() => {
    const from = dateFrom <= dateTo ? dateFrom : dateTo, to = dateFrom <= dateTo ? dateTo : dateFrom;
    const inWeek = days.filter((d) => d >= from && d <= to);
    return inWeek.length ? inWeek : [from];
  })();
  const grossHours = Math.max(0, (hmToMin(end) - hmToMin(start)) / 60);
  const shiftCount = Math.max(1, targetIds.length) * dateRange.length;
  const timeInvalid = hmToMin(end) <= hmToMin(start);

  // Debounced server evaluation of the draft.
  useEffect(() => {
    if (timeInvalid) { setEvaluation(null); setEvalError(null); return; }
    let live = true;
    setEvaluating(true);
    const timer = window.setTimeout(() => {
      evaluateShiftRules({
        staffIds: targetIds, locationId: state.shift.locationId,
        dateFrom: dateRange[0], dateTo: dateRange[dateRange.length - 1], start, end, role,
      })
        .then((res) => {
          if (!live) return;
          if (res && Array.isArray(res.checks)) { setEvaluation(res); setEvalError(null); }
          else { setEvaluation(null); setEvalError(t("workforce.schedule.evalNoChecks", "the service answered without any rule checks")); }
        })
        .catch((e: unknown) => { if (live) { setEvaluation(null); setEvalError(reasonOf(e)); } })
        .finally(() => { if (live) setEvaluating(false); });
    }, 400);
    return () => { live = false; window.clearTimeout(timer); };
  }, [targetIds.join(","), dateRange[0], dateRange[dateRange.length - 1], start, end, role, timeInvalid, state.shift.locationId]);

  const blocked = (checks ?? []).some((c) => c.status === "block");
  const warned = (checks ?? []).some((c) => c.status === "warn");
  const canSave = busyId === null && !timeInvalid && !!role.trim() && !blocked && (!warned || ack)
    && (applies === "individual" || targetIds.length > 0);

  const lbl: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 6, display: "block" };
  const section: CSSProperties = { display: "flex", flexDirection: "column", gap: 8, paddingBottom: 16, borderBottom: `1px solid ${T.n6}` };
  const CHECK_TONE: Record<string, string> = { ok: T.green1, auto: T.tDarkBlue, warn: T.orange1, block: T.red1 };

  const submit = () => {
    const ids: (string | null)[] = targetIds.length ? targetIds : [null];
    const drafts: WfShift[] = state.isNew
      ? ids.flatMap((id) => dateRange.map((date) => ({
          ...state.shift, id: "", date, start, end, role,
          staffId: id, staffName: id ? staff.find((s) => s.id === id)?.name ?? null : null,
          status: id ? ("draft" as const) : ("open" as const),
          breaks: evaluation?.breaks ?? state.shift.breaks,
        })))
      : [{ ...state.shift, start, end, role, date: dateRange[0],
          staffId: targetIds[0] ?? null,
          staffName: targetIds[0] ? staff.find((s) => s.id === targetIds[0])?.name ?? null : null }];
    void onSubmit(drafts, state.isNew);
  };

  return (
    <Drawer panel="wfschedule-shift" width={460} testid="wfschedule-shift-drawer" icon={<Icon.calendar size={16} />}
      title={state.isNew ? t("workforce.schedule.addShift", "Add shift") : t("workforce.workforceSchedule.editShift", "Edit shift")}
      sub={state.isNew
        ? `${shiftCount} ${shiftCount === 1 ? t("workforce.schedule.shiftLower", "shift") : t("workforce.schedule.shiftsLower", "shifts")} · ${dateRange.length} ${dateRange.length === 1 ? t("workforce.schedule.dayLower", "day") : t("workforce.schedule.daysLower", "days")}`
        : dayLabel(state.shift.date, "full")}
      onClose={onClose}
      footer={<>
        {onDelete ? <Btn variant="secondary" testid="wfschedule-drawer-delete" disabled={busyId !== null} onClick={() => { void onDelete(); }}>{busyId === "delete" ? t("workforce.workforceSchedule.removing", "Removing…") : t("common.actions.remove", "Remove")}</Btn> : null}
        <span style={{ flex: 1 }} />
        <Btn variant="secondary" testid="wfschedule-drawer-cancel" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn testid="wfschedule-drawer-save" disabled={!canSave} onClick={submit}>
          {busyId === "save" ? t("workforce.workforceSchedule.saving", "Saving…") : state.isNew ? t("workforce.schedule.addShift", "Add shift") : t("account.details.saveChanges", "Save changes")}
        </Btn>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Applies to */}
        {state.isNew ? (
          <div style={section}>
            <span style={lbl}>{t("workforce.schedule.appliesTo", "Applies to")}</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {([["individual", t("workforce.schedule.oneStaffMember", "One staff member")], ["role", t("workforce.schedule.byRole", "By role")], ["selected", t("workforce.schedule.selectedStaff", "Selected staff")]] as [Applies, string][]).map(([id, label]) => (
                <button key={id} data-testid="wfschedule-applies-mode" data-id={id} onClick={() => setApplies(id)}
                  style={{ padding: "6px 12px", minHeight: 32, borderRadius: 999, border: `1px solid ${applies === id ? T.p1 : T.border}`, background: applies === id ? T.p1 : T.white, color: applies === id ? T.white : T.n2, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{label}</button>
              ))}
            </div>
            {applies === "individual" ? (
              <FilterDropdown
                label={selected[0] ? staff.find((s) => s.id === selected[0])?.name ?? t("chrome.routes.staff", "Staff") : t("workforce.workforceSchedule.openShiftUnassigned", "Open shift (unassigned)")}
                active={!!selected[0]}
                options={[t("workforce.workforceSchedule.openShiftUnassigned", "Open shift (unassigned)"), ...staff.map((s) => s.name)]}
                onPick={(name) => { const hit = staff.find((s) => s.name === name); setSelected(hit ? [hit.id] : []); }}
                onClear={() => setSelected([])}
              />
            ) : applies === "role" ? (
              <>
                <FilterDropdown label={roleTarget ?? t("workforce.schedule.chooseARole", "Choose a role")} active={!!roleTarget} options={roles} onPick={setRoleTarget} onClear={() => setRoleTarget(null)} />
                <span style={{ fontSize: 11.5, color: T.soft }}>{targetIds.length} {t("workforce.schedule.staffMatchThisRole", "staff match this role")}</span>
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 168, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 6, padding: 6 }}>
                {staff.map((s) => {
                  const on = selected.includes(s.id);
                  return (
                    <button key={s.id} data-testid="wfschedule-staff-pick" data-id={s.id}
                      onClick={() => setSelected((prev) => on ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", minHeight: 36, borderRadius: 5, border: "none", background: on ? alpha(T.p1, 8) : "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                      <span style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{on ? <Icon.check size={10} /> : null}</span>
                      <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{s.name}</span>
                      <span style={{ fontSize: 11.5, color: T.soft, marginLeft: "auto" }}>{s.role || "—"}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div style={section}>
            <span style={lbl}>{t("workforce.schedule.assignedTo", "Assigned to")}</span>
            <FilterDropdown
              label={selected[0] ? staff.find((s) => s.id === selected[0])?.name ?? state.shift.staffName ?? t("chrome.routes.staff", "Staff") : t("workforce.workforceSchedule.openShiftUnassigned", "Open shift (unassigned)")}
              active={!!selected[0]}
              options={[t("workforce.workforceSchedule.openShiftUnassigned", "Open shift (unassigned)"), ...staff.map((s) => s.name)]}
              onPick={(name) => { const hit = staff.find((s) => s.name === name); setSelected(hit ? [hit.id] : []); }}
              onClear={() => setSelected([])}
            />
          </div>
        )}

        {/* Date range */}
        <div style={section}>
          <span style={lbl}>{state.isNew ? t("workforce.schedule.dateRange", "Date range") : t("workforce.schedule.date", "Date")}</span>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <FilterDropdown label={dayLabel(dateFrom)} active options={days.map((d) => dayLabel(d))} onPick={(v) => { const hit = days.find((d) => dayLabel(d) === v); if (hit) { setDateFrom(hit); if (hit > dateTo) setDateTo(hit); } }} onClear={() => setDateFrom(state.shift.date)} />
            </div>
            {state.isNew ? (
              <div style={{ flex: 1 }}>
                <FilterDropdown label={dayLabel(dateTo)} active options={days.filter((d) => d >= dateFrom).map((d) => dayLabel(d))} onPick={(v) => { const hit = days.find((d) => dayLabel(d) === v); if (hit) setDateTo(hit); }} onClear={() => setDateTo(dateFrom)} />
              </div>
            ) : null}
          </div>
        </div>

        {/* Times */}
        <div style={section}>
          <span style={lbl}>{t("workforce.schedule.times", "Times")}</span>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 11.5, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.schedule.starts", "Starts")}</span>
              <FilterDropdown label={hmLabel(start)} active options={timeOptions} onPick={(v) => setStart(labelToHm(v))} onClear={() => setStart(state.shift.start)} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 11.5, color: T.soft, display: "block", marginBottom: 4 }}>{t("workforce.schedule.ends", "Ends")}</span>
              <FilterDropdown label={hmLabel(end)} active options={timeOptions} onPick={(v) => setEnd(labelToHm(v))} onClear={() => setEnd(state.shift.end)} />
            </div>
          </div>
          {timeInvalid ? <span style={{ fontSize: 12, color: T.red1 }}>{t("workforce.schedule.endTimeMustBeAfterTheStartTime", "End time must be after the start time.")}</span>
            : <span style={{ fontSize: 11.5, color: T.soft }}>{grossHours.toFixed(grossHours % 1 ? 1 : 0)}{t("workforce.schedule.h", "h")}{" "}{t("workforce.schedule.perShift", "per shift")}{" "}{evaluation?.breaks.length ? ` · ${evaluation.breaks.length} ${t("workforce.schedule.breaksApplied", "breaks applied by the rules")}` : ""}</span>}
          <div>
            <span style={lbl}>{t("workforce.schedule.role", "Role")}</span>
            <Input value={role} placeholder={t("workforce.schedule.eGAdvisorCheckInConcierge", "e.g. Advisor, Check-in, Concierge")} onChange={(e) => setRole(e.target.value)} testid="wfschedule-drawer-role-input" />
          </div>
        </div>

        {/* Compliance */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={lbl}>{t("workforce.schedule.compliance", "Compliance")}</span>
          {evaluating ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.schedule.checkingRules", "Checking the rules…")}</span> : null}
          {checks ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="wfschedule-compliance-checks">
              {checks.map((c) => (
                <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: CHECK_TONE[c.status] ?? T.n4, marginTop: 5, flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{c.name} <span style={{ color: T.soft, fontWeight: 400, fontFamily: T.fontMono, fontSize: 11 }}>{c.param}</span></span>
                    <span style={{ display: "block", fontSize: 11.5, color: T.n2 }}>{c.msg}</span>
                  </span>
                </div>
              ))}
              {warned ? (
                <button data-testid="wfschedule-compliance-ack" onClick={() => setAck((v) => !v)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", minHeight: 44, border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                  <span style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${ack ? T.p1 : T.n4}`, background: ack ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{ack ? <Icon.check size={10} /> : null}</span>
                  <span style={{ fontSize: 12.5, color: T.n1 }}>{t("workforce.schedule.acknowledgeWarnings", "I've read the warnings and want to save anyway")}</span>
                </button>
              ) : null}
            </div>
          ) : unchecked ? (
            <div style={{ padding: "10px 12px", borderRadius: 6, background: alpha(T.orange1, 8), border: `1px solid ${alpha(T.orange1, 30)}` , display: "flex", flexDirection: "column", gap: 4 }} data-testid="wfschedule-compliance-unavailable">
              <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t("workforce.schedule.complianceNotChecked", "This draft has not been checked")}</span>
              <span style={{ fontSize: 11.5, color: T.n2 }}>{t("workforce.schedule.complianceUnavailableBody", "No rule-evaluation service answered, so nothing was verified against the rules below. Save is still allowed — the schedule's own validation runs when the week is published.")}</span>
              <span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono }}>evaluateShiftRules · {evalError ?? t("workforce.schedule.evalNoResponse", "no response")}</span>
            </div>
          ) : null}
          {laborRules.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>{unchecked ? t("workforce.schedule.rulesNotChecked", "Rules that were not checked") : t("workforce.schedule.rulesInForce", "Rules in force")}</span>
              {laborRules.filter((r) => r.enabled).map((r) => (
                <span key={r.id} style={{ fontSize: 11.5, color: T.n2, display: "flex", gap: 6 }}>
                  <span style={{ color: T.n1, fontWeight: 600 }}>{r.label}</span>
                  <span style={{ fontFamily: T.fontMono, color: T.soft }}>{Object.entries(r.params).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
